import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import { ProgramPurseResource } from '@/queries/program_purses/schemas.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shad/ui/tabs'
import { ArrowLeft } from 'lucide-react'
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'

interface PurseDetailsProps {
  purse: ProgramPurseResource
}

// Helper function to create outlet context - the type is automatically inferred
function createPurseDetailOutletContext(purse: ProgramPurseResource) {
  return { purse }
}

// Export the type derived from the context creator function
export type PurseDetailOutletContext = ReturnType<
  typeof createPurseDetailOutletContext
>

export function PurseDetailUI({ purse }: PurseDetailsProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { purseId } = useParams()

  // Extract tab from URL path
  const getActiveTab = () => {
    const pathSegments = location.pathname.split('/')
    const lastSegment = pathSegments[pathSegments.length - 1]
    if (
      [
        'overview',
        'academic_terms',
        'distribution',
        'transactions',
        'distribution-history',
      ].includes(lastSegment)
    ) {
      return lastSegment
    }
    return 'overview'
  }

  const activeTab = getActiveTab()

  // Handle tab navigation with React Router
  const handleTabChange = (tab: string) => {
    const urlQueryString = location.search || ''
    navigate(`/purse_settings/${purseId}/${tab}${urlQueryString}`)
  }

  return (
    <div className="space-y-6">
      {/*Back Button*/}
      <div className="mb-6 flex items-center gap-4">
        <Link
          to={AppRoutePaths.purse_settings.index}
          className={buttonVariants({
            variant: 'outline',
            className: 'space-x-2',
          })}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Purses
        </Link>
      </div>

      {/*Page Header*/}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{purse.name}</h1>
          <p className="text-gray-600">
            Manage purse details and distribution settings
          </p>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="academic_terms">Academic Terms</TabsTrigger>
          <TabsTrigger value="distribution">Distribution Settings</TabsTrigger>
          <TabsTrigger value="distribution-history">
            Distribution History
          </TabsTrigger>
          <TabsTrigger value="transactions">Transaction Log</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-6">
          {/** Overview, Distribution Settings, Transaction Logs*/}
          <Outlet context={createPurseDetailOutletContext(purse)} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
